// Package imports:
import React, { useMemo, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import Highcharts from 'highcharts/highstock';
import ISLocale from 'date-fns/locale/is';
import { format } from 'date-fns';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import LmdTable from '../../ui-elements/Table/LmdTable';
import Avatar, { AvatarNames } from '../../ui-elements/Avatar/Avatar';
// Service imports:
import { useApiLmdData, useApiLmdDataMappedByString } from '../../services/apiHooks';
import { getAPItoday, changeIssuerName, formatNumber } from '../../services/utils';
import { SHORT_MONTHS } from '../../services/config';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdFundPricePublic } from '../../types/FundsTypes';
import { IApiHistoryTimeseriesShortDATA } from '../../types/MarketTypes';

interface IOwnProps {
    symbol?: string,
    title?: string,
    height?: number
}

interface IVisibleFund {
    symbol: string,
    name: string,
    color: string,
    data: number[][]
}

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const FundsChart: React.FC<Props> = ({
    title,
    height = 500,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    // Data for the table: list of all funds. We use their symbols to fetch chart data with the hook
    const [ tableData ] = useApiLmdData<IApiLmdFundPricePublic[]>(
        `/v1/market_data/v1/funds/*/prices/public?value_date=${getAPItoday()}`,
        accessToken,
        refreshRateMs
    );
    // List of funds that are visible in the chart.
    const [visibleChartFunds, setVisibleChartFunds] = useState<IVisibleFund[]>([{
        symbol: 'isl_equus',
        name: 'IS EQUUS Hlutabréf',
        color: '#dc1e35',
        data: []
    }]);
    const handleTableData = useMemo(() => {
        const { data, error } = tableData;
        if (data === null) return tableData;

        const filterData = (groupname: string) => {
            return data.filter(x => {
                return x.group_key === groupname && x.fund_type !== 'S'
            });
        }
        const sortFundPrice = (a: IApiLmdFundPricePublic, b: IApiLmdFundPricePublic) => {
            if (a.change12m === null && b.change12m === null) return 0;
            else if (a.change12m === null) return 1;
            else if (b.change12m === null) return -1;
            else return b.change12m - a.change12m;
        }
        return {
            data: {
                "Hæsta ávöxtun síðustu 12 mánuði á rekstrarfélag": filterData('top_12m').sort(sortFundPrice),
                "Skammtímasjóðir": filterData('short_term').sort(sortFundPrice),
                "Íslensk skuldabréf - stutt": filterData('is_bonds_short').sort(sortFundPrice),
                "Íslensk skuldabréf - meðallöng": filterData('is_bonds_medium').sort(sortFundPrice),
                "Íslensk skuldabréf - löng": filterData('is_bonds_long').sort(sortFundPrice),
                "Íslensk hlutabréf": filterData('is_equity').sort(sortFundPrice),
                "Erlend hlutabréf": filterData('foreign_equity').sort(sortFundPrice),
                "Erlend hlutabréf - ISK": filterData('foreign_equity_isk').sort(sortFundPrice),
                "Erlend skuldabréf": filterData('foreign_bonds').sort(sortFundPrice),
                "Blandaðir sjóðir": filterData('mixed').sort(sortFundPrice)
            },
            error
        }
    },[tableData])
    
    useEffect(() => {
        if(handleTableData.data !== null){
            //@ts-ignore
            const highestSymbol = handleTableData.data["Hæsta ávöxtun síðustu 12 mánuði á rekstrarfélag"][0].symbol;
            //@ts-ignore
            const highestName = handleTableData.data["Hæsta ávöxtun síðustu 12 mánuði á rekstrarfélag"][0].name;
            setVisibleChartFunds([{
                symbol: highestSymbol,
                name: highestName,
                color: '#dc1e35',
                data: []
            }])
        }
    },[handleTableData]) 
    
    // Hook used for fetching chart data for clicked symbol.
    const [symbolToFetchFor, setSymbolToFetchFor, newDataForFetchedSymbol] = useApiLmdDataMappedByString<IApiHistoryTimeseriesShortDATA>(
        visibleChartFunds[0]?.symbol ?? '',
        (letter: string) => `/v1/market_data/v1/funds/${letter}/history_timeseries?from_date=1972-02-26&to_date=${getAPItoday()}`,
        accessToken,
        refreshRateMs
    );
    useEffect(() => {
        if(handleTableData.data !== null){
            //@ts-ignore
            const highestSymbol = handleTableData.data["Hæsta ávöxtun síðustu 12 mánuði á rekstrarfélag"][0].symbol;
            setSymbolToFetchFor(highestSymbol)
        }
    }, [handleTableData])
    

    const [selectedRange, setSelectedRange] = useState(4);

    // Ref to the div where we render the chart.
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const existingVisibleChartFundIndex = visibleChartFunds.findIndex(visibleChartFund => visibleChartFund.symbol === symbolToFetchFor);
        if (existingVisibleChartFundIndex !== -1 && newDataForFetchedSymbol.data !== null) {
            const existingVisibleChartFund = visibleChartFunds[existingVisibleChartFundIndex];
            const newVisibleChartFunds = [...visibleChartFunds];
            newVisibleChartFunds.splice(existingVisibleChartFundIndex, 1, {
                ...existingVisibleChartFund,
                name: newDataForFetchedSymbol.data.name,
                data: newDataForFetchedSymbol.data.data
            })
            setVisibleChartFunds(newVisibleChartFunds);
        }
    }, [symbolToFetchFor, newDataForFetchedSymbol]);

    

    useEffect(() => {
        let allSeries: Highcharts.SeriesLineOptions[] = visibleChartFunds.map((fund, i) => {
             return ({
                id: fund.symbol,
                type: 'line',
                name: fund.name,
                color: fund.color,
                data: fund.data ?? []
            })
        })

        Highcharts.setOptions({
            lang: {
                shortMonths: SHORT_MONTHS,
                resetZoom: 'Til baka',
                resetZoomTitle: ''
            }
        });

        if (divRef.current !== null) {
            Highcharts.stockChart(divRef.current, {
                chart: {
                    height: height,
                    marginTop: 80,
                    reflow: true,
                    style: {
                        fontFamily: "Roboto"
                    },
                    zoomType: "xy",
                    selectionMarkerFill: '#4569EE18',
                    resetZoomButton: {
                        position: {
                            x: 0,
                            y: -75
                        },
                        theme: {
                            r: 6,
                            height: 28
                        }
                    },
                    plotBackgroundColor: {
                        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                        stops: [
                            [0.05, '#FFF'],
                            [0.95, '#F8F8FA']
                        ]
                    }
                },
                exporting: {
                    buttons: {
                        contextButton: {
                            x: 30,
                            y: 36
                        }
                    }
                },
                credits: {
                    enabled: false
                },
                title: {
                    text: '',
                    align: 'center',
                    x: 60,
                    margin: -14
                },
                navigator: {
                    enabled: true,
                    adaptToUpdatedData: false,
                    handles: {
                        height: 22,
                        width: 6
                    },
                    maskFill: '#D4DEFF80',
                    series: {
                        lineWidth: 0,
                        opacity: 0
                    },
                    height: 20,
                    xAxis: {
                        labels: {
                            style: {
                                color: '#7B7C83',
                                fontSize: '10px'
                            }
                        },
                        gridLineWidth: 0,
                        minTickInterval: 1000 * 60 * 60 * 24 * 365,
                        lineWidth: 0
                    },
                    yAxis: {
                        lineWidth: 0
                    },
                    outlineWidth: 0
                },
                scrollbar: {
                    enabled: false
                },
                tooltip: {
                    enabled: true,
                    formatter: function() {
                        //filtera einungis það sem er visible af því ef það er notað points[0] crashar allt þegar eitthvað series er disable-að
                        return (
                            `<div class="chart-tooltip">
                                <p class="tooltip-date">${format(this.x, 'EEEE, dd. MMM yyyy', {locale: ISLocale})}</p>
                                ${this.points?.filter(x => x.point.visible === true).map(x => (
                                    `<p><span style="color: ${x.color}">${x.series.getName()}</span>: ${formatNumber(x.y, '-', 1)}</p>`
                                ))}
                            </div>`
                        ).replace(/>,</g,'><')
                    },
                    borderWidth: 0,
                    shadow: false,
                    useHTML: true,
                    shared: true
                },
                xAxis: [{
                    type: 'datetime',
                    title: {
                        text: null
                    },
                    minTickInterval: 1000 * 60 * 60 * 24 * 30,
                    tickWidth: 0
                }],
                yAxis: [{
                    title: {
                        text: 'Verð (kr.)',
                        margin: 15
                    },
                    minTickInterval: 1,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false,
                    height: '100%'
                }
                ],
                plotOptions: {
                    series: {
                        color: '#4569EE',
                        enableMouseTracking: true,
                        marker: {
                            enabled: false
                        },
                        animation: false,
                        turboThreshold: 0,
                        compare: (allSeries.length > 1) ? 'percent' : undefined
                    }
                },
                legend: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'top',
                    itemStyle: {
                        color: '#232530',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '26px'
                    },
                    y: 10
                },
                rangeSelector: {
                    allButtonsEnabled: true,
                    buttonPosition: {
                        align: 'right',
                        x: 0,
                        y: -80
                    },
                    buttons: [{
                        type: 'month',
                        count: 1,
                        text: '1 mán.',
                        title: '1 mánuður',
                        events: {
                            click: () => setSelectedRange(0)
                        }
                    }, {
                        type: 'month',
                        count: 3,
                        text: '3 mán.',
                        title: '3 mánuðir',
                        events: {
                            click: () => setSelectedRange(1)
                        }
                    }, {
                        type: 'month',
                        count: 6,
                        text: '6 mán.',
                        title: '6 mánuðir',
                        events: {
                            click: () => setSelectedRange(2)
                        }
                    }, {
                        type: 'ytd',
                        text: 'Þetta ár',
                        title: 'Þetta ár',
                        events: {
                            click: () => setSelectedRange(3)
                        }
                    }, {
                        type: 'year',
                        count: 1,
                        text: '1 ár',
                        title: '1 ár',
                        events: {
                            click: () => setSelectedRange(4)
                        }
                    }, {
                        type: 'year',
                        count: 3,
                        text: '3 ár',
                        title: '3 ár',
                        events: {
                            click: () => setSelectedRange(5)
                        }
                    }, {
                        type: 'year',
                        count: 5,
                        text: '5 ár',
                        title: '5 ár',
                        events: {
                            click: () => setSelectedRange(6)
                        }
                    }, {
                        type: 'all',
                        text: 'Allt',
                        title: 'Allt',
                        events: {
                            click: () => setSelectedRange(7)
                        }
                    }],
                    buttonSpacing: 5,
                    buttonTheme: {
                        r: 6,
                        height: 25,
                        width: 50
                    },
                    inputEnabled: false,
                    selected: selectedRange,
                    labelStyle: {
                        display: 'none'
                    },
                    dropdown: 'never'
                },
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: 480
                        },
                        chartOptions: {
                            rangeSelector: {
                                dropdown: 'always'
                            }
                        }
                    }]
                },
                series: allSeries
            }, () => {});
        }
    }, [visibleChartFunds, divRef.current, tableData]);

    const handleTableFundClick = (symbol: string | null) => {
        if (symbol === null) return;
        if (newDataForFetchedSymbol.data === null && newDataForFetchedSymbol.error === null) return;
        const existingChartFund = visibleChartFunds.find(chartFund => chartFund.symbol === symbol);
        if (existingChartFund === undefined) {
            if (symbol === symbolToFetchFor) {
                setVisibleChartFunds(visibleChartFunds.concat([{
                    symbol,
                    name: newDataForFetchedSymbol.data?.name ?? '',
                    data: newDataForFetchedSymbol.data?.data ?? [],
                    color: `#${((Math.floor(Math.random() * 16777215)).toString(16)).padStart(6, '0')}`
                }]))
            } else {
                setVisibleChartFunds(visibleChartFunds.concat([{
                    symbol,
                    name: 'Hleður...',
                    color: `#${((Math.floor(Math.random() * 16777215)).toString(16)).padStart(6, '0')}`,
                    data: []
                }]));
                setSymbolToFetchFor(symbol);
            }
        } else {
            setVisibleChartFunds(visibleChartFunds.filter(chartFund => chartFund.symbol !== symbol));
        }
    }

    return (
        <DisplayBox
            className='KCL_chart'
            title={title}
            {...props}
        >
            <div className='row'>
                <div className='col-lg-9 col-sm-12'>
                    {/* {dataStyringA instanceof Error ?
                        <ErrorAlert error={dataStyringA} /> : dataStyringA === null ?
                            <Loading /> : */}
                            <div className="chart" ref={divRef}></div>
                    {/* } */}
                </div>
                <div className='col-lg-3 col-sm-12'>
                    <LmdTable
                        apiData={handleTableData}
                        columns={[{
                            title: '',
                            // sortable:  ,
                            dataAccessor: ({ symbol, issuer_name, name }) => (
                                <div
                                    className={cx('avatar-and-link', 'avatar-text-link', {
                                        'selected': (visibleChartFunds.findIndex(chartFund => chartFund.symbol === symbol) !== -1)
                                    })}
                                    onClick={() => handleTableFundClick(symbol)}
                                >
                                    <Avatar
                                        name={changeIssuerName(issuer_name) as AvatarNames}
                                        size='sm'
                                        color
                                        backgroundGrey
                                    />
                                    <span>
                                        {name}
                                    </span>
                                </div>
                                // <div
                                //     onClick={() => handleTableFundClick(Symbol)}
                                // >
                                //     <Avatar
                                //         name={changeIssuerName(IssuerName) as AvatarNames}
                                //         size='sm'
                                //         color
                                //         backgroundGrey
                                //     />
                                //     <span>{Name}</span>
                                // </div>
                            ),
                            textAlign: 'left'
                        }]}
                        expandableRowConfig={{
                            defaultExpandedIndexes: [0],
                            showNumberOfItemsInExpandable: true
                        }}
                    />
                </div>
            </div>
        </DisplayBox>
    );
}

export default FundsChart;